"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Input } from "./input"
import { Textarea } from "./textarea"

const inputGroupVariants = cva(
  "has-disabled:bg-input/50 dark:has-disabled:bg-input/80 rounded-lg border transition-all in-data-[slot=combobox-content]:focus-within:border-inherit has-disabled:opacity-50 has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-end]]:[&_input]:pt-3 has-[>[data-align=block-start]]:[&_input]:pb-3 has-[>[data-align=inline-end]]:[&_input]:pr-1.5 has-[>[data-align=inline-start]]:[&_input]:pl-1.5 group/input-group relative flex w-full min-w-0 items-center outline-none has-[textarea]:h-auto has-[[data-slot=input-group-floating-label][data-floating=false]]:[&_input]:placeholder:text-transparent has-[[data-slot=input-group-floating-label][data-floating=false]]:[&_input::-webkit-datetime-edit]:text-transparent has-[[data-slot=input-group-floating-label][data-floating=false]]:[&_input::-webkit-date-and-time-value]:text-transparent",
  {
    variants: {
      variant: {
        default: "border-input dark:bg-input/30 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30 has-[[aria-invalid=true]]:border-destructive has-[[aria-invalid=true]]:focus-within:border-destructive has-[[aria-invalid=true]]:focus-within:ring-destructive/30",
        standout: "bg-white/15 border-transparent text-white/90 placeholder:text-white/50 focus-within:bg-background focus-within:text-foreground focus-within:border-input focus-within:[&_[data-slot=input-group-addon]]:text-muted-foreground [&_[data-slot=input-group-addon]]:text-white/60 [&_[data-slot=input-group-floating-label]]:text-white/60 focus-within:[&_[data-slot=input-group-floating-label]]:text-foreground [&_input]:text-inherit [&_input]:placeholder:text-white/50 focus-within:[&_input]:text-foreground focus-within:[&_input]:placeholder:text-muted-foreground",
      },
      size: {
        default: "h-8",
        xs: "h-6 rounded-[min(var(--radius-md),10px)] [&_input]:text-xs [&_[data-slot=input-group-addon]]:text-xs [&_[data-slot=input-group-addon]]:pl-1.5 [&_[data-slot=input-group-addon]]:pr-1.5",
        sm: "h-7 rounded-[min(var(--radius-md),12px)] [&_input]:text-sm [&_[data-slot=input-group-addon]]:text-sm",
        lg: "h-9 [&_input]:text-base",
        xl: "h-11 rounded-xl [&_input]:text-base [&_input]:px-3 [&_[data-slot=input-group-addon]]:px-3 [&_svg:not([class*='size-'])]:size-5",
        "2xl": "h-14 rounded-2xl [&_input]:text-lg [&_input]:px-4 [&_[data-slot=input-group-addon]]:px-3.5 [&_svg:not([class*='size-'])]:size-5",
        "3xl": "h-16 rounded-2xl [&_input]:text-xl [&_input]:px-5 [&_[data-slot=input-group-addon]]:px-4 [&_svg:not([class*='size-'])]:size-6",
        "4xl": "h-20 rounded-3xl [&_input]:text-2xl [&_input]:px-6 [&_[data-slot=input-group-addon]]:px-5 [&_svg:not([class*='size-'])]:size-7",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

type InputGroupSize = NonNullable<VariantProps<typeof inputGroupVariants>["size"]>
type InputGroupVariant = NonNullable<VariantProps<typeof inputGroupVariants>["variant"]>

function InputGroup({
  className,
  size = "default",
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & { size?: InputGroupSize; variant?: InputGroupVariant }) {
  return (
    <div
      data-slot="input-group"
      data-size={size}
      data-variant={variant}
      role="group"
      className={cn(inputGroupVariants({ size, variant, className }))}
      {...props}
    />
  )
}

const inputGroupAddonVariants = cva(
  "text-muted-foreground h-auto gap-2 py-1.5 text-sm font-medium group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc((var(--radius)-5px)*var(--brm))] [&>svg:not([class*='size-'])]:size-4 flex cursor-text items-center justify-center select-none",
  {
    variants: {
      align: {
        "inline-start": "pl-2 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem] order-first",
        "inline-end": "pr-2 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem] order-last",
        "block-start":
          "px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2 order-first w-full justify-start",
        "block-end":
          "px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2 order-last w-full justify-start",
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  }
)

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) {
          return
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus()
      }}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") {
          return
        }
        if ((e.target as HTMLElement).closest("button")) {
          return
        }
        e.preventDefault()
        e.currentTarget.parentElement?.querySelector("input")?.focus()
      }}
      {...props}
    />
  )
}

const inputGroupButtonVariants = cva(
  "gap-2 text-sm shadow-none flex items-center",
  {
    variants: {
      size: {
        xs: "h-6 gap-1 rounded-[calc((var(--radius)-3px)*var(--brm))] px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
        sm: "",
        "icon-xs": "size-6 rounded-[calc((var(--radius)-3px)*var(--brm))] p-0 has-[>svg]:p-0",
        "icon-sm": "size-8 p-0 has-[>svg]:p-0",
      },
    },
    defaultVariants: {
      size: "xs",
    },
  }
)

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size" | "type"> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: "button" | "submit" | "reset"
  }) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  )
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "text-muted-foreground gap-2 text-sm [&_svg:not([class*='size-'])]:size-4 flex items-center [&_svg]:pointer-events-none",
        className
      )}
      {...props}
    />
  )
}

function InputGroupInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn("rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent flex-1", className)}
      {...props}
    />
  )
}

function InputGroupTextarea({
  className,
  ...props
}: React.ComponentProps<"textarea">) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn("rounded-none border-0 bg-transparent py-2 shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent flex-1 resize-none", className)}
      {...props}
    />
  )
}

type FloatingLabelSize = "default" | "sm" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl"

interface InputGroupFloatingLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  htmlFor: string
  size?: FloatingLabelSize
  /**
   * Keep the label in its floated position regardless of the control's value.
   * Use for controls whose value cannot be read from a native `value`
   * (select triggers, comboboxes) so the label never overlaps the selection.
   */
  floating?: boolean
}

const floatingLabelSizeStyles: Record<FloatingLabelSize, { floating: string; resting: string; leftOffset: number }> = {
  default: {
    floating: "text-[10px] px-2 left-1",
    resting: "text-base",
    leftOffset: 12,
  },
  sm: {
    floating: "text-[9px] px-1.5 left-0.5",
    resting: "text-sm",
    leftOffset: 10,
  },
  lg: {
    floating: "text-[10px] px-2 left-1",
    resting: "text-base",
    leftOffset: 16,
  },
  xl: {
    floating: "text-[11px] px-2.5 left-1.5",
    resting: "text-base",
    leftOffset: 20,
  },
  "2xl": {
    floating: "text-xs px-3 left-2",
    resting: "text-lg",
    leftOffset: 24,
  },
  "3xl": {
    floating: "text-sm px-3 left-2",
    resting: "text-xl",
    leftOffset: 28,
  },
  "4xl": {
    floating: "text-base px-4 left-3",
    resting: "text-2xl",
    leftOffset: 32,
  },
  "5xl": {
    floating: "text-lg px-4 left-3",
    resting: "text-3xl",
    leftOffset: 40,
  },
  "6xl": {
    floating: "text-xl px-5 left-4",
    resting: "text-4xl",
    leftOffset: 48,
  },
}

const InputGroupFloatingLabel = React.forwardRef<
  HTMLLabelElement,
  InputGroupFloatingLabelProps
>(({ className, htmlFor, children, size = "default", floating, ...props }, ref) => {
  const [detectedFloating, setIsFloating] = React.useState(false)
  const isFloating = floating === true || detectedFloating
  const [leftAddonWidth, setLeftAddonWidth] = React.useState(0)

  const sizeStyles = floatingLabelSizeStyles[size]

  React.useEffect(function syncIsFloating() {
    const input = document.getElementById(htmlFor)
    if (!input) return

    const checkInputState = () => {
      const hasValue = (input as HTMLInputElement).value !== ""
      const isFocused = document.activeElement === input
      setIsFloating(hasValue || isFocused)
    }

    const calculateAddonWidth = () => {
      const inputGroup = input.closest('[data-slot="input-group"]')
      if (inputGroup) {
        const leftAddons = inputGroup.querySelectorAll(
          '[data-slot="input-group-addon"][data-align="inline-start"]'
        )
        if (leftAddons.length > 0) {
          let totalWidth = 0
          leftAddons.forEach((addon) => {
            totalWidth += addon.getBoundingClientRect().width
          })
          setLeftAddonWidth(totalWidth)
        } else {
          setLeftAddonWidth(0)
        }
      }
    }

    const handleFocus = () => setIsFloating(true)
    const handleBlur = () => {
      const hasValue = (input as HTMLInputElement).value !== ""
      setIsFloating(hasValue)
    }
    const handleInput = () => {
      const hasValue = (input as HTMLInputElement).value !== ""
      setIsFloating(hasValue)
    }
    const handleChange = () => {
      const hasValue = (input as HTMLInputElement).value !== ""
      setIsFloating(hasValue)
    }

    checkInputState()
    calculateAddonWidth()

    input.addEventListener("focus", handleFocus)
    input.addEventListener("blur", handleBlur)
    input.addEventListener("input", handleInput)
    input.addEventListener("change", handleChange)

    const attributeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "value"
        ) {
          checkInputState()
        }
      })
    })

    attributeObserver.observe(input, {
      attributes: true,
      attributeFilter: ["value"],
    })

    const pollInterval = setInterval(checkInputState, 100)

    const structureObserver = new MutationObserver(calculateAddonWidth)
    const inputGroup = input.closest('[data-slot="input-group"]')
    if (inputGroup) {
      structureObserver.observe(inputGroup, { childList: true, subtree: true })
    }

    return function unbindEventListeners() {
      input.removeEventListener("focus", handleFocus)
      input.removeEventListener("blur", handleBlur)
      input.removeEventListener("input", handleInput)
      input.removeEventListener("change", handleChange)
      attributeObserver.disconnect()
      structureObserver.disconnect()
      clearInterval(pollInterval)
    }
  }, [htmlFor])

  return (
    <label
      ref={ref}
      htmlFor={htmlFor}
      data-slot="input-group-floating-label"
      data-floating={isFloating ? "true" : "false"}
      className={cn(
        "absolute pointer-events-none transition-all duration-200 ease-in-out z-10",
        isFloating
          ? cn(
              "bg-background/50 group-has-disabled:bg-muted rounded-full backdrop-blur-md text-foreground top-0 block -translate-y-1/2 font-medium group-has-disabled:opacity-50",
              sizeStyles.floating
            )
          : cn(
              "font-normal text-muted-foreground top-1/2 -translate-y-1/2",
              sizeStyles.resting
            ),
        className
      )}
      style={
        isFloating
          ? { transformOrigin: "left center" }
          : {
              transformOrigin: "left center",
              left: `${leftAddonWidth > 0 ? leftAddonWidth + 8 : sizeStyles.leftOffset}px`,
            }
      }
      {...props}
    >
      {children}
    </label>
  )
})

InputGroupFloatingLabel.displayName = "InputGroupFloatingLabel"

export {
  InputGroup,
  inputGroupVariants,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
  InputGroupFloatingLabel,
  type InputGroupSize,
  type InputGroupVariant,
}
